import { KnownProjectTeamMemberDtoAssociatedBrand } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { BusinessCenter, Mail, Smartphone } from '@mui/icons-material';
import parsePhoneNumberFromString from 'libphonenumber-js/mobile';
import { Link } from 'react-router-dom';
import { PermissionBoundary } from '../../../../../../core/permissions/permission-boundary.component';
import { ActionsButton } from '../../../actions-button/actions-button.component';
import { LoadedActionsButtonItem } from '../../../actions-button/actions-button.types';
import { Popover } from '../../../popover/popover.component';
import { useTeamMemberDetailsPageHeaderStyles } from './team-member-details-page-header.styles';

export interface TeamMemberDetailsPageHeaderProps {
  name: string;
  accessLevel: string;
  accessLevelDescription: string;
  status: string;
  role?: string;
  mobile?: string;
  email?: string;
  externalID?: string;
  actions: LoadedActionsButtonItem[];
  userDirectoryId?: string;
  associatedBrand?: KnownProjectTeamMemberDtoAssociatedBrand;
  projectMemberships?: { projectName: string; status: string; link: string }[];
}

export function TeamMemberDetailsPageHeader({
  name,
  accessLevel,
  accessLevelDescription,
  status,
  role,
  mobile,
  email,
  externalID,
  actions,
  userDirectoryId,
  associatedBrand,
  projectMemberships
}: TeamMemberDetailsPageHeaderProps) {
  let phoneNumber: string | undefined = mobile;
  if (phoneNumber) {
    try {
      phoneNumber = parsePhoneNumberFromString(phoneNumber)?.formatInternational();
    } catch (e) {
    } finally {
      if (phoneNumber === undefined) {
        phoneNumber = mobile;
      }
    }
  }
  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useTeamMemberDetailsPageHeaderStyles();
  return (
    <Grid container sx={{ flexDirection: isSM ? 'column' : 'row' }} className={classes.root}>
      <Grid
        container
        sx={{ flexDirection: 'column' }}
        size={{
          md: 4
        }}
      >
        <Grid>
          <h2>{name}</h2>
        </Grid>
        <Grid>
          <p className={classes.subtitle}>
            Access Level: {accessLevel}
            <Popover title={`${accessLevel} Access Level`} text={accessLevelDescription} />
          </p>
        </Grid>
        <Grid>
          <p className={classes.status}>{status}</p>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{ flexDirection: 'column' }}
        size={{
          md: 4
        }}
      >
        {role ? (
          <Grid>
            <p className={classes.info}>
              <BusinessCenter /> {role}
            </p>
          </Grid>
        ) : null}
        {mobile ? (
          <Grid>
            <p className={classes.info}>
              <Smartphone /> {mobile}
            </p>
          </Grid>
        ) : null}
        {email ? (
          <Grid>
            <p className={classes.info}>
              <Mail /> {email}
            </p>
          </Grid>
        ) : null}
        {externalID ? (
          <Grid>
            <p className={classes.info}>External ID: {externalID}</p>
          </Grid>
        ) : null}
        <PermissionBoundary availableUiFeatures={['internal-groundfloor']}>
          {userDirectoryId ? (
            <Grid>
              <p className={classes.info}>WorkOS ID: {userDirectoryId}</p>
            </Grid>
          ) : null}
          {associatedBrand ? (
            <Grid>
              <p className={classes.info}>Brand: {getAssociatedBrandLabel(associatedBrand)}</p>
            </Grid>
          ) : null}
        </PermissionBoundary>
      </Grid>
      {!isSM && projectMemberships && projectMemberships.length > 0 && (
        <Grid
          container
          sx={{ flexDirection: 'column' }}
          size={{
            md: 4
          }}
        >
          <Grid>
            <p className={classes.membershipsLabel}>OTHER PROJECTS</p>
          </Grid>
          {projectMemberships.map((membership) => (
            <Grid key={membership.link}>
              <p className={classes.membershipRow}>
                <Link to={membership.link} className={classes.membershipLink}>
                  {membership.projectName}
                </Link>
                <span className={classes.membershipStatus}> ({membership.status})</span>
              </p>
            </Grid>
          ))}
        </Grid>
      )}
      <div className={classes.actionButtonBox}>
        <ActionsButton itemArray={actions} />
      </div>
    </Grid>
  );
}

function getAssociatedBrandLabel(associatedBrand: KnownProjectTeamMemberDtoAssociatedBrand) {
  switch (associatedBrand) {
    case KnownProjectTeamMemberDtoAssociatedBrand.Groundfloor:
      return 'Groundfloor';
    case KnownProjectTeamMemberDtoAssociatedBrand.Lastk:
      return 'Last K';
    default:
      return '';
  }
}
